<template>
    <ChatLayout>
        <div class="h-full overflow-y-auto bg-slate-50">
            <div class="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
                <div class="mb-8">
                    <h1 class="text-3xl font-bold tracking-tight text-slate-900">Clipfinder API</h1>
                    <p v-if="enabled" class="mt-2 text-slate-600">
                        Create an API key and retrieve structured Clipfinder search results from your applications.
                    </p>
                </div>

                <section v-if="!enabled" class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" aria-labelledby="api-unavailable-heading">
                    <h2 id="api-unavailable-heading" class="text-lg font-semibold text-slate-900">Not available for this account yet</h2>
                    <p class="mt-3 text-slate-700">
                        Clipfinder has an API that runs the same search you use in the chat, programmatically, with structured results.
                    </p>
                    <p class="mt-3 text-slate-700">
                        API access for this account is not available yet. It will become available later through a subscription.
                    </p>
                </section>

                <div v-else class="grid gap-6 lg:grid-cols-3">
                    <section class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" aria-labelledby="api-keys-heading">
                        <h2 id="api-keys-heading" class="text-lg font-semibold text-slate-900">API keys</h2>
                        <p class="mt-1 text-sm text-slate-600">The complete key is shown only once when it is created.</p>

                        <form class="mt-5 flex flex-col gap-3 sm:flex-row" @submit.prevent="createKey">
                            <label for="api-key-name" class="sr-only">API key name</label>
                            <input
                                id="api-key-name"
                                v-model="name"
                                required
                                maxlength="80"
                                :disabled="creating || Boolean(plaintextKey)"
                                class="min-w-0 flex-1 rounded-lg border-slate-300 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-slate-100"
                                placeholder="Key name, for example Production"
                            />
                            <button
                                type="submit"
                                :disabled="creating || Boolean(plaintextKey)"
                                class="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                                {{ creating ? 'Creating...' : 'Create key' }}
                            </button>
                        </form>
                        <p v-if="createError" class="mt-2 text-sm text-red-700" role="alert">{{ createError }}</p>

                        <div v-if="plaintextKey" class="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4" aria-labelledby="new-key-heading">
                            <p id="new-key-heading" class="font-semibold text-amber-900">Copy this key now. It cannot be shown again.</p>
                            <div class="mt-3 flex flex-col gap-2 sm:flex-row">
                                <textarea
                                    ref="plaintextKeyField"
                                    :value="plaintextKey"
                                    readonly
                                    rows="3"
                                    aria-label="New API key"
                                    class="min-w-0 flex-1 resize-none break-all rounded border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800"
                                    @focus="selectKeyText"
                                ></textarea>
                                <button
                                    type="button"
                                    class="rounded-lg border border-amber-400 px-3 py-2 text-sm font-semibold text-amber-900"
                                    @click="copyKey"
                                >
                                    {{ copied ? 'Copied' : 'Copy key' }}
                                </button>
                            </div>
                            <p v-if="copyError" class="mt-2 text-sm text-red-700" role="alert">{{ copyError }}</p>
                            <button type="button" class="mt-4 text-sm font-semibold text-amber-950 underline" @click="confirmKeySaved">
                                I have stored this key securely
                            </button>
                        </div>

                        <p class="sr-only" aria-live="polite">{{ announcement }}</p>
                        <p v-if="revokeError" class="mt-4 text-sm text-red-700" role="alert">{{ revokeError }}</p>

                        <div class="mt-6 divide-y divide-slate-200 border-t border-slate-200">
                            <div v-if="keys.length === 0" class="py-6 text-sm text-slate-500">No API keys yet.</div>
                            <div v-for="key in keys" :key="key.id" class="flex items-center justify-between gap-4 py-4">
                                <div class="min-w-0">
                                    <p class="truncate font-medium text-slate-900">{{ key.name }}</p>
                                    <p class="text-xs text-slate-500">
                                        Created {{ formatDate(key.created_at) }}. Last used
                                        {{ key.last_used_at ? formatDate(key.last_used_at) : 'never' }}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    :disabled="revokingId === key.id"
                                    class="shrink-0 text-sm font-semibold text-red-700 hover:text-red-900 disabled:opacity-50"
                                    :aria-label="`Revoke API key ${key.name}`"
                                    @click="revokeKey(key)"
                                >
                                    {{ revokingId === key.id ? 'Revoking...' : 'Revoke' }}
                                </button>
                            </div>
                        </div>
                    </section>

                    <aside class="space-y-6">
                        <section v-if="quota" class="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" aria-labelledby="quota-heading">
                            <h2 id="quota-heading" class="font-semibold text-slate-900">Daily search quota</h2>
                            <p class="mt-3 text-3xl font-bold text-slate-900">{{ quota.remaining }}</p>
                            <p class="text-sm text-slate-600">of {{ quota.limit }} searches remaining today</p>
                            <div
                                class="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"
                                role="progressbar"
                                aria-label="Daily API searches used"
                                :aria-valuemin="0"
                                :aria-valuemax="quota.limit"
                                :aria-valuenow="quotaUsedClamped"
                            >
                                <div class="h-full bg-primary-600" :style="{ width: `${quotaPercentage}%` }"></div>
                            </div>
                            <p class="mt-2 text-xs text-slate-500">Resets {{ formatReset(quota.reset_at) }}</p>
                        </section>

                        <section class="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-700 shadow-sm">
                            <h2 class="font-semibold text-slate-900">Quick start</h2>
                            <ol class="mt-3 list-decimal space-y-2 pl-5">
                                <li>List available channels with <code>GET /api/v1/podcasters</code>.</li>
                                <li>Start a search with <code>POST /api/v1/clipfinder/searches</code>.</li>
                                <li>Poll the Location URL until status is completed.</li>
                            </ol>
                            <p class="mt-4">Send the key as <code>Authorization: Bearer &lt;key&gt;</code>.</p>
                            <p class="mt-3">
                                The same key works with remote MCP at <code>https://api.clipfinder.com/mcp/clipfinder</code> for Workbench, Claude,
                                Codex and other MCP clients.
                            </p>
                            <a
                                class="mt-4 inline-block font-semibold text-primary-700 hover:text-primary-900"
                                href="/account/api/docs"
                                target="_blank"
                                rel="noopener noreferrer"
                                >Read API documentation</a
                            >
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    </ChatLayout>
</template>

<script setup lang="ts">
import Cookies from 'universal-cookie'
import ChatLayout from '@/Layouts/ChatLayout.vue'
import { defineUserStore } from '@/Stores/user'
import { computed, ref } from 'vue'

type ApiKey = {
    id: number
    name: string
    created_at: string
    last_used_at: string | null
}

const props = defineProps<{
    enabled: boolean
    keys: ApiKey[]
    quota: { limit: number; used: number; remaining: number; reset_at: number } | null
}>()

defineUserStore()

const keys = ref<ApiKey[]>([...props.keys])
const name = ref('')
const plaintextKey = ref('')
const creating = ref(false)
const copied = ref(false)
const createError = ref('')
const copyError = ref('')
const revokeError = ref('')
const announcement = ref('')
const revokingId = ref<number | null>(null)
const plaintextKeyField = ref<HTMLTextAreaElement | null>(null)
const quotaUsedClamped = computed(() => (props.quota ? Math.min(props.quota.limit, Math.max(0, props.quota.used)) : 0))
const quotaPercentage = computed(() => (props.quota ? Math.min(100, Math.max(0, (quotaUsedClamped.value / props.quota.limit) * 100)) : 0))

// The XSRF-TOKEN cookie, not a meta tag: the Inertia root view (resources/views/app.blade.php)
// carries no <meta name="csrf-token">, only the Blade layouts do, so reading one here sent an
// empty X-CSRF-TOKEN and every create and revoke came back 419 in a browser. The feature tests
// never caught it because Laravel skips CSRF verification under `php artisan test`. This mirrors
// resources/js/Composables/useApi.ts, which is how the rest of the SPA authenticates its writes.
const csrfToken = () => new Cookies().get('XSRF-TOKEN') ?? ''

const createKey = async () => {
    if (plaintextKey.value) return

    creating.value = true
    createError.value = ''

    try {
        const response = await fetch('/account/api/keys', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-XSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({ name: name.value }),
        })
        const body = await response.json()

        if (!response.ok) {
            throw new Error(body.errors?.name?.[0] ?? body.message ?? 'Could not create the API key.')
        }

        keys.value.unshift(body.data)
        plaintextKey.value = body.plaintext_key
        name.value = ''
        copied.value = false
        announcement.value = `API key ${body.data.name} created. Store the displayed key securely.`
    } catch (caught) {
        createError.value = caught instanceof Error ? caught.message : 'Could not create the API key.'
    } finally {
        creating.value = false
    }
}

const copyKey = async () => {
    copyError.value = ''

    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(plaintextKey.value)
        } else {
            selectKeyText()

            if (!document.execCommand('copy')) throw new Error('Clipboard copy failed')
        }
        copied.value = true
        announcement.value = 'API key copied to the clipboard.'
    } catch {
        copyError.value = 'The key could not be copied. Select and copy it manually.'
    }
}

const selectKeyText = () => {
    plaintextKeyField.value?.focus()
    plaintextKeyField.value?.select()
}

const confirmKeySaved = () => {
    plaintextKey.value = ''
    copied.value = false
    copyError.value = ''
    announcement.value = 'The one-time API key display has been closed.'
}

const revokeKey = async (key: ApiKey) => {
    if (!window.confirm(`Revoke the API key “${key.name}”? Applications using it will stop working immediately.`)) return

    revokeError.value = ''
    revokingId.value = key.id

    try {
        const response = await fetch(`/account/api/keys/${key.id}`, {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-XSRF-TOKEN': csrfToken() },
        })

        if (!response.ok) {
            const body = await response.json().catch(() => ({}))
            throw new Error(body.message ?? 'The API key could not be revoked.')
        }

        keys.value = keys.value.filter((current) => current.id !== key.id)
        announcement.value = `API key ${key.name} revoked.`
    } catch (caught) {
        revokeError.value = caught instanceof Error ? caught.message : 'The API key could not be revoked.'
    } finally {
        revokingId.value = null
    }
}

const formatDate = (value: string) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
const formatReset = (timestamp: number) =>
    new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(timestamp * 1000))
</script>
